<template>
  <div
    id="assess-progress"
    class="assessment-progress tw-bg-[#ffffff] tw-max-w-[241px] tw-p-[25px] tw-pr-[9.5px] tw-w-full tw-flex tw-flex-col tw-justify-between tw-items-start tw-flex-[1_0_0] tw-self-stretch tw-border tw-shadow-[0px_0px_4px_0px_rgba(0,0,0,0.05)] tw-rounded-[6px] tw-border-solid tw-border-[#EFF0F1]"
  >
    <div class="sticky-header">
      <div
        class="tw-font-Poppins tw-text-[12px] tw-font-[600] tw-text-[#211F58] tw-uppercase tw-leading-[100%] tw-tracking-[1.2px]"
      >
        {{ title }}
      </div>
      <table v-if="showAll" class="tw-w-full tw-mt-[15px] tw-pr-[10px]">
        <tbody>
          <tr>
            <th colspan="3" class="sticky-searchbox-inner">
              <BaseSearchbox
                ref="searchboxRef"
                :class="[
                  'md:tw-w-full tw-relative tw-h-[36px] tw-rounded-[6px] tw-border tw-border-solid tw-border-[#EFF0F1] tw-bg-[#FFFFFF] tw-p-[15px] tw-pr-[15px] search-box',
                ]"
                input-style="tw-bg-[#FFFFFF] !tw-text-[13px] tw-mt-[1px]"
                :icon-search-style="'tw-scale-[0.867]'"
                :icon-close-style="'tw-scale-[0.8]'"
                :model-value="searchValue"
                :placeholder="getComponentUiText('app-header-search-prompt-1')"
                @update:model-value="actionsUpdateFilterCategory"
                @on:focus="() => {}"
                @on:blur="() => {}"
                @keyup.enter="handleNavigateToCategory"
              />
            </th>
          </tr>
        </tbody>
      </table>
    </div>

    <table
      id="progress-box"
      :class="[
        'category-container tw-w-full tw-flex tw-flex-col tw-mb-0 tw-border-separate tw-border-spacing-y-[17px] 2xl:!tw-overflow-auto',
        showAll ? 'show-all' : 'show-less',
      ]"
    >
      <tbody>
        <tr
          v-for="(item, index) in categoryFormatted"
          :key="index"
          :class="[
            'tw-cursor-pointer tw-select-none  tw-text-[15px] tw-text-[#211F58] tw-h-fit',
            activeCategory === item.checklistItemCategoryId
              ? 'tw-font-[600]'
              : '',
            index === categoryFormatted.length - 1 ? '' : '',
          ]"
          @click="updateActiveCategory(item)"
        >
          <td class="tw-pr-[8px] tw-text-left tw-align-top tw-w-auto">
            {{ item.indexLabel }}
          </td>

          <td class="tw-align-top tw-pr-[5px]">
            <span class="progress-text">
              {{ item.checklistItemCategoryLabel }}
            </span>
          </td>

          <td class="tw-align-top tw-w-fit tw-pr-[5px]">
            <!-- "span" tag to add id for target b-popover -->
            <span
              :id="
                'assessment-circular-progress-' + item.checklistItemCategoryId
              "
            >
              <circularProgress
                :class="['circular-progress']"
                :percent="item.checklistItemCompletedPcntNumber"
              />
            </span>

            <b-popover
              :target="
                'assessment-circular-progress-' + item.checklistItemCategoryId
              "
              triggers="hover"
              placement="right"
              custom-class="assessment-progress-popover"
              :offset="12"
              :delay="{
                show: 0,
                hide: 100,
              }"
              @show="true"
            >
              <div
                class="tw-px-[5px] tw-py-[3px] tw-bg-[#E4F1FF] tw-text-[#211F58] tw-rounded-[4px] tw-text-[11px] tw-font-medium tw-font-[Poppins]"
              >
                <span>{{ item.checklistItemCategoryLabel }}</span>
                <br />
                <span>{{
                  getComponentUiText(
                    "app-component-assessment-progress-tooltip-text-1",
                    false,
                    {
                      percent: Math.round(
                        item.checklistItemCompletedPcntNumber || 0,
                      ),
                    },
                  )
                }}</span>
              </div>
            </b-popover>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-show="
        searchValue.length === 0 &&
        progressCategories.length > NUMBER_SHOW_CATEGORIES
      "
      :class="[
        'tw-select-none tw-cursor-pointer tw-flex tw-gap-[5px] tw-items-center tw-px-[5px] tw-text-[11px] tw-text-[#387DFF] tw-bg-[#F2F8FF] tw-font-medium tw-rounded',
      ]"
      @click="handleShowAll"
    >
      <span v-show="!showAll">{{
        getComponentUiText(
          "app-component-assessments-progress-show-all-1",
          false,
          { numberCategoryHide: progressCategories.length?.toString() },
        )
      }}</span>
      <span v-show="showAll">{{
        getComponentUiText("app-component-assessments-progress-hide-all-1")
      }}</span>
      <ArrowIcon
        :class="['right-arrow tw-mr-[2px]', showAll ? 'show-all' : '']"
        :width="6"
        :height="9"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ICategories } from "@/interfaces/assessments/ICategories";
import circularProgress from "./circularProgress.vue";
import {
  Ref,
  computed,
  onMounted,
  ref,
  watch,
  onBeforeMount,
  onUnmounted,
  nextTick,
} from "vue";
import store from "@/store";
import ArrowIcon from "../svgs/arrowIcon.vue";
import { getComponentUiText } from "@/utils/utilities";
import { localStorageKey } from "@/utils/constants";
import BaseSearchbox from "../common/form/baseSearchbox.vue";
import { debounce } from "lodash";

const props = defineProps({
  /** The title of assessment progress */
  title: {
    type: String,
    default: "",
  },
  /** Progress categories */
  progressCategories: {
    type: Array as () => ICategories[],
    default: () => [],
  },
});

const activeCategory = computed(() => store.state.Assessment.activeCategory);
const NUMBER_SHOW_CATEGORIES = 5;
const showAll: Ref<boolean> = ref(false);
const searchValue: Ref<string> = ref("");
const categoryFormatted: Ref<ICategories[]> = ref([]);
const searchboxRef = ref();

const handleCompleteValue = (categories: ICategories[]) => {
  categories.map(
    (c) =>
      (c.checklistItemCompletedPcntNumber = (Number(
        c.checklistItemCompletedPcnt,
      ) * 100) as number),
  );
};

const emit = defineEmits(["scrollToTop"]);

const updateActiveCategory = async (category: ICategories) => {
  if (activeCategory.value === category.checklistItemCategoryId) return;
  localStorage.setItem(
    localStorageKey.assessmentActiveCategory,
    category.checklistItemCategoryId,
  );
  store.dispatch(
    "Assessment/changeActiveCategory",
    category.checklistItemCategoryId,
  );
  emit("scrollToTop");
};

const scrollToActiveCate = (index = 0) => {
  if (index < 3 || showAll.value) return;
  const scrollTop = (index - 2) * 43;
  const progressBox = document.getElementById("progress-box");
  if (!progressBox) return;
  progressBox.scrollTop = scrollTop;
};

const handleActiveCategory = () => {
  const activeIndex = props.progressCategories.findIndex(
    (cate) => cate.checklistItemCategoryId === activeCategory.value,
  );
  scrollToActiveCate(activeIndex);
};

const handleShowAll = () => {
  showAll.value = !showAll.value;
  searchValue.value = "";
  categoryFormatted.value = props.progressCategories.map((item, index) => ({
    ...item,
    indexLabel: index + 1 + ".",
  }));

  if (showAll.value) {
    nextTick(() => {
      // Focus the input inside BaseSearchbox when showAll is true
      // Try to access the input element inside BaseSearchbox
      const inputEl =
        searchboxRef.value?.$el?.querySelector("input") ||
        searchboxRef.value?.$refs?.input ||
        searchboxRef.value?.input;
      if (inputEl && typeof inputEl.focus === "function") {
        inputEl.focus();
      }
    });
  } else {
    setTimeout(() => {
      handleActiveCategory();
    }, 200);
  }
};

const handleFilterCategory = () => {
  if (!searchValue.value) {
    categoryFormatted.value = props.progressCategories.map((item, index) => ({
      ...item,
      indexLabel: index + 1 + ".",
    }));
    return;
  }

  categoryFormatted.value = props.progressCategories
    .map((item, index) => ({
      ...item,
      indexLabel: index + 1 + ".",
    }))
    .filter((item) =>
      item.checklistItemCategoryLabel
        .toLowerCase()
        .includes(searchValue.value.toLowerCase()),
    );
};

const handleNavigateToCategory = () => {
  if (categoryFormatted.value.length !== 1) return;
  updateActiveCategory(categoryFormatted.value[0]);
};

const actionsUpdateFilterCategory = debounce((value: string) => {
  searchValue.value = value;
  handleFilterCategory();
}, 300);

onBeforeMount(() => {
  const localActiveCategory = localStorage.getItem(
    localStorageKey.assessmentActiveCategory,
  );
  if (localActiveCategory) {
    store.dispatch("Assessment/changeActiveCategory", localActiveCategory);
  }
});

onMounted(() => {
  handleCompleteValue(props.progressCategories);
  handleFilterCategory();
});

onUnmounted(() => {
  localStorage.removeItem(localStorageKey.assessmentActiveCategory);
});

watch(() => activeCategory.value, handleActiveCategory);

watch(
  () => props.progressCategories,
  () => {
    handleCompleteValue(props.progressCategories);
    handleFilterCategory();
  },
);

watch(showAll, (val) => {
  if (val) {
    nextTick(() => {
      const inputEl =
        searchboxRef.value?.$el?.querySelector("input") ||
        searchboxRef.value?.$refs?.input ||
        searchboxRef.value?.input;
      if (inputEl && typeof inputEl.focus === "function") {
        inputEl.focus();
      }
    });
  }
});
</script>

<style scoped lang="scss">
.assessment-progress {
  width: 241px;

  ol {
    list-style-type: none;
    padding-left: 0px !important;
  }

  .category-container {
    height: 199px;
    overflow: auto;

    &.show-all {
      height: auto;
      overflow: hidden;
      padding-right: 6px;
    }
  }

  /* width */
  ::-webkit-scrollbar {
    width: 6px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    border-radius: 8px;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: #eff0f1;
    border-radius: 8px;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #eff0f1;
  }

  .right-arrow {
    transition: transform 0.3s ease;
    transform: rotate(90deg) scale(0.6);

    &.show-all {
      transform: rotate(-90deg) scale(0.6);
    }
  }
}

/* Make the searchbox sticky */
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  padding-bottom: 8px;
  box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.04);

  .sticky-searchbox-inner {
    background: #fff;
    position: static;
    box-shadow: none;
    padding: 0;
    padding-right: 15px;
  }
}

.show-less {
  .progress-text {
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

:deep(.assessment-progress-popover .popover-arrow) {
  height: 7px;
  width: 7px;
  background-color: #e4f1ff;
  transform: rotate(45deg);
}

:deep(.assessment-progress-popover.bs-popover-end) {
  margin-top: unset;
  max-width: 100%;
  font-family: Poppins;

  .popover-arrow {
    left: -4px;
  }
}

:deep(.assessment-progress-popover.bs-popover-start) {
  max-width: 100%;
  font-family: Poppins;

  .popover-arrow {
    right: -4px;
  }
}

:deep(.assessment-progress-popover .popover-arrow)::before,
:deep(.assessment-progress-popover .popover-arrow)::after {
  display: none !important;
}

@media screen and (min-width: 1536px) {
  .category-container {
    max-height: calc(100vh - 425px);
  }
}
</style>
